import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useFormContext } from 'react-hook-form'
import { Pressable, Text, View } from 'react-native'
import { tv } from 'tailwind-variants'
import { match } from 'ts-pattern'

import { AnimatedPrice } from '@flows-ui/components/AnimatedPrice'
import { useQuoteData } from '@flows-ui/services/QuoteDataContext'
import type { PremiumInterval } from '@flows-ui/services/QuoteDataContext.types'
import { getDiscountPercent, resolveAppliedDiscount } from '@flows-ui/utils/discounts'
import { getSelectedCoveragePlan } from '@flows-ui/utils/getSelectedCoveragePlan'
import { getSinglePriceWithInterval } from '@flows-ui/utils/getSinglePriceWithInterval'
import { useTranslation } from '@flows-ui/utils/i18n'
import { EVENTS } from '@flows/configs/analytics/constants'
import type { ProductKey } from '@flows/index'
import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import Spinner from '@ui-kit/components/Spinner'
import { COLOR } from '@ui-kit/constants'
import { body1, body2, h1 } from '@ui-kit/core/typography'

export const PREMIUM_INTERVALS: readonly PremiumInterval[] = ['P1M', 'P1Y']

type Props = {
  productKey: ProductKey
  allowedPremiumIntervals?: readonly PremiumInterval[]
}

const priceStyles = tv({
  slots: {
    card: 'w-[449px] max-w-full items-center self-center overflow-visible rounded-lg border border-grey-200 bg-grey-100 px-8 py-16',
    content: 'w-full items-center overflow-visible',
    discountBadge: 'mb-3 rounded-full bg-success px-4 py-1.5',
    breakdown: 'mt-6 w-full',
    divider: 'mb-2 border-t border-grey-200',
    dividerSpaced: 'my-1 border-t border-grey-200',
    breakdownRow: 'flex-row justify-between py-1',
  },
  variants: {
    hasBreakdown: {
      true: { card: 'py-10' },
    },
  },
})

const intervalSelectorStyles = tv({
  slots: {
    row: 'mt-2.5 flex-row items-center gap-2.5 lg:gap-4',
    trigger: 'flex-row items-center gap-x-0.5 rounded bg-black py-1 pl-2.5 pr-1.5',
    staticInterval: 'flex-row items-center rounded bg-black px-2.5 py-1',
    dropdown: 'fixed z-[99999] overflow-hidden rounded-lg bg-white shadow-lg',
    viewport: 'px-1 py-2',
    item: 'rounded px-2 py-2',
  },
  variants: {
    selected: {
      true: { item: 'border-2 border-black' },
      false: {},
    },
  },
})

const IntervalSelector = ({
  finalPrice,
  intervalShort,
  selectedInterval,
  onChangeInterval,
  dropdownOpen,
  setDropdownOpen,
  allowedPremiumIntervals = PREMIUM_INTERVALS,
}: {
  finalPrice: string
  intervalShort: string
  selectedInterval: string
  onChangeInterval: (v: string) => void
  dropdownOpen: boolean
  setDropdownOpen: (v: boolean) => void
  allowedPremiumIntervals?: readonly PremiumInterval[]
}) => {
  const { t } = useTranslation()
  const styles = intervalSelectorStyles()
  const btnRef = useRef<View>(null)
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 })

  useEffect(() => {
    if (!dropdownOpen) {
      return
    }
    const syncPosition = () => {
      const node = btnRef.current as unknown as HTMLElement | null
      if (!node) {
        return
      }
      const rect = node.getBoundingClientRect()
      setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width })
    }
    syncPosition()
    window.addEventListener('scroll', syncPosition, true)
    window.addEventListener('resize', syncPosition)
    return () => {
      window.removeEventListener('scroll', syncPosition, true)
      window.removeEventListener('resize', syncPosition)
    }
  }, [dropdownOpen])

  const options = PREMIUM_INTERVALS.filter((interval) =>
    allowedPremiumIntervals.includes(interval)
  ).map((interval) => ({
    value: interval,
    label: t(`sharedFlows_common:orderSummary.quote.interval.${interval}`),
  }))

  const isSelectable = options.length > 1

  return (
    <View className={styles.row()}>
      <AnimatedPrice price={finalPrice} className={h1({ color: 'black' })} testID="headerPrice" />
      {isSelectable ? (
        <Pressable
          ref={btnRef}
          className={styles.trigger()}
          onPress={() => setDropdownOpen(!dropdownOpen)}
        >
          <Text className={body2({ color: 'white' })}>{intervalShort}</Text>
          <PhosphorIcon name="CaretDown" size={24} color={COLOR.white} />
        </Pressable>
      ) : (
        <View className={styles.staticInterval()} testID="headerPriceInterval">
          <Text className={body2({ color: 'white' })}>{intervalShort}</Text>
        </View>
      )}
      {isSelectable &&
        dropdownOpen &&
        createPortal(
          <div
            className={styles.dropdown()}
            style={{ top: pos.top, left: pos.left, width: pos.width }}
          >
            <View className={styles.viewport()}>
              {options.map((opt) => (
                <Pressable
                  key={opt.value}
                  className={intervalSelectorStyles({
                    selected: opt.value === selectedInterval,
                  }).item()}
                  onPress={() => onChangeInterval(opt.value)}
                >
                  <Text className={body2({ color: 'black' })}>{opt.label}</Text>
                </Pressable>
              ))}
            </View>
          </div>,
          document.body
        )}
    </View>
  )
}

export const Price = (props: Props) => {
  const { t } = useTranslation()
  const { setValue, watch } = useFormContext()
  const {
    priceData,
    isFetchingPrice,
    premiumInterval,
    productGeneration,
    permanentDiscount,
    referralDiscount,
    couponInfo,
    formatCurrency,
    trackEvent,
    trackPriceChange,
  } = useQuoteData()
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const tariffLine = watch(`products.${props.productKey}.configuration_parameters.tariff_line`) as
    | string
    | undefined
  const configurationParameters = watch(`products.${props.productKey}.configuration_parameters`) as
    | Record<string, unknown>
    | undefined
  const selectedPlan = getSelectedCoveragePlan({ configurationParameters, productGeneration })
  const grossPremium = Number.parseFloat(
    getSinglePriceWithInterval({
      premiumInterval,
      priceData,
      productKey: props.productKey,
      tariffLine,
      configurationId: selectedPlan?.itemKey?.current,
    })
  )

  const appliedDiscount = resolveAppliedDiscount({
    couponInfo,
    grossPremium,
    permanentDiscount,
    referralDiscount,
  })
  const hasBreakdown = appliedDiscount.type !== 'none'

  const handleIntervalChange = (value: string) => {
    setValue('premium_interval', value)
    setDropdownOpen(false)
    trackEvent(EVENTS.ToggleSwitched, { toggleName: 'premium_interval', value })
    trackPriceChange('payment_frequency_change')
  }

  const intervalShort =
    premiumInterval === 'P1M'
      ? t('sharedFlows_common:orderSummary.quote.interval.P1M')
      : t('sharedFlows_common:orderSummary.quote.interval.P1Y')

  const badgeText = match(appliedDiscount)
    .with({ type: 'coupon' }, (discount) =>
      t('sharedFlows_common:orderSummary.checkout.discountBadge', {
        amount: formatCurrency(discount.amount),
      })
    )
    .with({ type: 'permanent' }, (discount) =>
      t('sharedFlows_common:orderSummary.quote.summary.permanentDiscountBanner', {
        discountPercent: `${getDiscountPercent({ amount: discount.amount, grossPremium })}%`,
      })
    )
    .otherwise(() => undefined)

  const discountRowLabel = match(appliedDiscount.type)
    .with('permanent', () =>
      t(`sharedFlows_common:orderSummary.checkout.permanentDiscount.${premiumInterval}`)
    )
    .with('referral', () => t('sharedFlows_common:orderSummary.checkout.referralCredit'))
    .otherwise(() => t('sharedFlows_common:orderSummary.checkout.coupon'))

  const finalPriceLabel = match(appliedDiscount.type)
    .with('coupon', () =>
      premiumInterval === 'P1Y'
        ? t('sharedFlows_common:orderSummary.checkout.priceFirstYear')
        : t('sharedFlows_common:orderSummary.checkout.priceFirstMonth')
    )
    .otherwise(() => t('sharedFlows_common:orderSummary.checkout.effectivePrice'))

  const { card, content, discountBadge, breakdown, divider, dividerSpaced, breakdownRow } =
    priceStyles({ hasBreakdown })

  return (
    <View className={card()}>
      {!priceData && isFetchingPrice ? (
        <Spinner size="small" />
      ) : (
        <View className={content()}>
          {badgeText ? (
            <View className={discountBadge()}>
              <Text className={body2({ color: 'white', bold: true })}>{badgeText}</Text>
            </View>
          ) : null}
          <IntervalSelector
            finalPrice={formatCurrency(appliedDiscount.finalPrice)}
            intervalShort={intervalShort}
            selectedInterval={premiumInterval}
            onChangeInterval={handleIntervalChange}
            dropdownOpen={dropdownOpen}
            setDropdownOpen={setDropdownOpen}
            allowedPremiumIntervals={props.allowedPremiumIntervals}
          />
          {hasBreakdown ? (
            <View className={breakdown()}>
              <View className={divider()} />
              <View className={breakdownRow()}>
                <Text className={body2({ color: 'grey700' })}>
                  {t('sharedFlows_common:orderSummary.checkout.insurancePremium')}
                </Text>
                <AnimatedPrice
                  price={formatCurrency(grossPremium)}
                  className={body2({ bold: true })}
                />
              </View>
              <View className={breakdownRow()}>
                <Text className={body2({ color: 'grey700' })}>{discountRowLabel}</Text>
                <AnimatedPrice
                  price={`-${formatCurrency(appliedDiscount.amount)}`}
                  className={body2({ color: 'success', bold: true })}
                />
              </View>
              <View className={breakdownRow()}>
                <Text className={body1({ color: 'black', bold: true })}>{finalPriceLabel}</Text>
                <AnimatedPrice
                  price={formatCurrency(appliedDiscount.finalPrice)}
                  className={body1({ bold: true })}
                />
              </View>
              {appliedDiscount.type === 'coupon' && (
                <>
                  <View className={dividerSpaced()} />
                  <View className={breakdownRow()}>
                    <Text className={body2({ color: 'grey700' })}>
                      {premiumInterval === 'P1Y'
                        ? t('sharedFlows_common:orderSummary.checkout.yearlyPriceAfterFirstYear')
                        : t('sharedFlows_common:orderSummary.checkout.monthlyPriceAfterFirstMonth')}
                    </Text>
                    <AnimatedPrice
                      price={formatCurrency(grossPremium)}
                      className={body2({ bold: true })}
                    />
                  </View>
                </>
              )}
            </View>
          ) : null}
        </View>
      )}
    </View>
  )
}
